import { osnovaApi } from '../base';

export interface CeoBankAccount {
  id: string;
  bank_name: string;
  bik: string;
  account_number: string;
  corr_account: string;
  account_type: 'settlement' | 'special_obs' | 'treasury' | 'deposit' | 'tax_reserve' | 'credit_line' | string;
  currency: string;
  entity_name: string;
  /** ИНН и КПП владельца — реквизиты плательщика в файле для банка. */
  entity_inn: string | null;
  entity_kpp: string | null;
  purpose_label: string;
  project_id: string | null;
  project_name: string | null;
  current_balance: number;
  available_balance: number;
  blocked_amount: number;
  overdraft_limit: number;
  interest_rate_pct: number;
  api_provider: string;
  api_status: 'connected' | 'syncing' | 'error' | 'manual' | string;
  api_external_id: string | null;
  api_last_sync_at: string | null;
  api_error_message: string | null;
  /** Подключение к банку. Нет подключения — счёт ведётся вручную. */
  connection_id: string | null;
  is_primary: boolean;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CeoBankAccountsSummary {
  total_current_balance: number;
  total_available_balance: number;
  total_blocked_amount: number;
  total_overdraft_limit: number;
  free_operational_cash: number;
  special_obs_balance: number;
  tax_reserve_balance: number;
  deposit_balance: number;
  connected_api_count: number;
  total_accounts_count: number;
  last_api_sync_at: string | null;
}

export interface CeoBankPayment {
  id: string;
  account_id: string | null;
  bank_name: string | null;
  account_number: string | null;
  payment_date: string;
  direction: 'inflow' | 'outflow' | string;
  amount: number;
  vat_amount: number;
  currency: string;
  counterparty_name: string;
  counterparty_inn: string | null;
  purpose: string;
  category: string;
  document_number: string | null;
  project_id: string | null;
  project_name: string | null;
  contract_id: string | null;
  contract_number: string | null;
  ks3_reference: string | null;
  debt_instrument_id: string | null;
  debt_title: string | null;
  status: string;
  source: string;
  external_tx_id: string | null;
  created_at: string;
}

export interface CeoPaymentCategoryBreakdown {
  category: string;
  category_label: string;
  direction: string;
  amount: number;
  share_pct: number;
  payments_count: number;
}

export interface CeoBankBalanceHistoryRow {
  id: string;
  account_id: string;
  bank_name: string | null;
  account_number: string | null;
  snapshot_date: string;
  opening_balance: number;
  inflow_amount: number;
  outflow_amount: number;
  closing_balance: number;
  source: string;
}

export interface CeoDailyPaymentsSummary {
  report_date: string;
  opening_balance: number;
  inflow_total: number;
  outflow_total: number;
  internal_transfer_total: number;
  net_cash_flow: number;
  closing_balance: number;
  inflow_count: number;
  outflow_count: number;
  internal_transfer_count: number;
  outflow_by_category: CeoPaymentCategoryBreakdown[];
  inflow_by_category: CeoPaymentCategoryBreakdown[];
  payments: CeoBankPayment[];
}

export interface CeoDebtInstrument {
  id: string;
  instrument_type:
    | 'credit_line_vkl'
    | 'credit_line_nkl'
    | 'term_loan'
    | 'overdraft'
    | 'project_finance'
    | 'leasing'
    | 'bank_guarantee'
    | string;
  contract_number: string;
  title: string;
  creditor_name: string;
  creditor_inn: string | null;
  borrower_entity: string;
  project_id: string | null;
  project_name: string | null;
  contract_id: string | null;
  settlement_account_id: string | null;
  settlement_bank_name: string | null;
  settlement_account_number: string | null;
  currency: string;
  total_limit: number;
  drawn_amount: number;
  available_limit: number;
  principal_balance: number;
  accrued_interest: number;
  rate_type: 'fixed' | 'floating_cbr' | 'subsidized' | string;
  base_rate_pct: number;
  margin_pct: number;
  effective_rate_pct: number;
  start_date: string;
  end_date: string;
  payment_day: number;
  leased_asset_name: string | null;
  leased_asset_count: number;
  residual_buyout_amount: number;
  collateral_summary: string | null;
  covenants_summary: string | null;
  covenant_status: 'ok' | 'warning' | 'breach' | string;
  status: string;
  responsible_person: string | null;
  notes: string | null;
  next_payment_date: string | null;
  next_payment_amount: number;
  overdue_amount: number;
  schedule_items_count: number;
  paid_items_count: number;
  created_at: string;
  updated_at: string;
}

export interface CeoDebtScheduleItem {
  id: string;
  instrument_id: string;
  instrument_title: string;
  instrument_type: string;
  contract_number: string;
  creditor_name: string;
  leased_asset_name: string | null;
  installment_no: number;
  due_date: string;
  principal_amount: number;
  interest_amount: number;
  fee_amount: number;
  total_payment: number;
  remaining_principal: number;
  paid_amount: number;
  paid_date: string | null;
  status: 'planned' | 'paid' | 'partially_paid' | 'overdue' | string;
  payment_reference: string | null;
  comment: string | null;
  days_until_due: number;
}

export interface CeoDebtInstrumentDetail {
  instrument: CeoDebtInstrument;
  cbr_key_rate_pct: number;
  recalculated_effective_rate_pct: number;
  total_scheduled_principal: number;
  total_scheduled_interest: number;
  total_scheduled_fees: number;
  total_paid_to_date: number;
  remaining_to_pay: number;
  schedule: CeoDebtScheduleItem[];
  related_payments: CeoBankPayment[];
}

export interface CeoDebtCalendarMonth {
  month_key: string;
  month_label: string;
  principal_total: number;
  interest_total: number;
  fee_total: number;
  credit_payments_total: number;
  leasing_payments_total: number;
  guarantee_fees_total: number;
  total_payment: number;
  paid_amount: number;
  unpaid_amount: number;
  items_count: number;
}

export interface CeoDebtPortfolioSummary {
  cbr_key_rate_pct: number;
  total_credit_limit: number;
  total_credit_drawn: number;
  available_credit_limit: number;
  total_credit_principal: number;
  total_leasing_principal: number;
  total_guarantees_amount: number;
  total_debt_burden: number;
  weighted_avg_rate_pct: number;
  payments_next_7d: number;
  payments_next_30d: number;
  payments_current_month: number;
  overdue_payments_total: number;
  leased_units_total: number;
  covenant_warnings_count: number;
  cash_coverage_ratio_30d: number;
  instruments: CeoDebtInstrument[];
  calendar_by_month: CeoDebtCalendarMonth[];
  upcoming_schedule: CeoDebtScheduleItem[];
}

// ── Загрузка кредитов и лизинга из Excel: файл → просмотр → сохранить ──

export type CeoDebtInstrumentType =
  | 'credit_line_vkl'
  | 'credit_line_nkl'
  | 'term_loan'
  | 'overdraft'
  | 'project_finance'
  | 'leasing'
  | 'bank_guarantee';

/** Строка графика платежей договора из файла. */
export interface CeoDebtImportScheduleRow {
  installment_no: number | null;
  /** YYYY-MM-DD */
  due_date: string;
  principal_amount: number | null;
  interest_amount: number | null;
  fee_amount: number | null;
  /** Итог платежа по графику, > 0. */
  total_payment: number;
  /** `null` — в таблице не сказано, оплачен ли платёж. */
  paid_amount: number | null;
  paid_date: string | null;
  remaining_principal: number | null;
  comment: string | null;
}

/** Договор, найденный в файле (ещё не сохранён). */
export interface CeoDebtImportContract {
  /** Уникальный ключ договора в этом файле. */
  key: string;
  /** Где найден: «Общая, стр. 5». */
  sources: string[];
  instrument_type: CeoDebtInstrumentType;
  /** Тип определён не по столбцу «Тип» — подсветить «проверьте». */
  type_guessed: boolean;
  contract_number: string;
  contract_date: string | null;
  title: string;
  creditor_name: string;
  creditor_inn: string | null;
  borrower_entity: string | null;
  currency: string | null;
  /** Сумма договора / лимит. */
  total_limit: number | null;
  drawn_amount: number | null;
  /** Остаток долга из таблицы; `null` — посчитает сервер по графику. */
  principal_balance: number | null;
  rate_type: 'fixed' | 'floating_cbr' | 'subsidized';
  base_rate_pct: number | null;
  margin_pct: number | null;
  start_date: string | null;
  end_date: string | null;
  payment_day: number | null;
  leased_asset_name: string | null;
  leased_asset_count: number | null;
  residual_buyout_amount: number | null;
  collateral_summary: string | null;
  covenants_summary: string | null;
  responsible_person: string | null;
  notes: string | null;
  status: 'active' | 'closed' | null;
  schedule: CeoDebtImportScheduleRow[];
  /**
   * Поля, выведенные программой, а не взятые из файла (тип по умолчанию,
   * наименование, срок и сумма по графику…): при обновлении договора в ОСНОВЕ
   * они не меняются.
   */
  derived: string[];
  /** Такой договор уже есть в ОСНОВЕ (номер + кредитор). */
  existing_id: string | null;
  existing_title: string | null;
  /** Договор загрузить нельзя (причины). */
  errors: string[];
  /** Загрузить можно, но проверить. */
  warnings: string[];
}

/** Лист файла: что из него взято. */
export interface CeoDebtImportSheet {
  name: string;
  layout: 'register' | 'schedule' | 'blocks' | 'skipped';
  /** Номер строки Excel с заголовками столбцов. */
  header_row: number | null;
  /** Узнанные столбцы — готовые подписи. */
  columns: string[];
  contracts: number;
  schedule_rows: number;
  /** Почему лист пропущен / что взято. */
  note: string | null;
}

export interface CeoDebtImportPreview {
  filename: string;
  /** YYYY-MM-DD — дата сервера. */
  today: string;
  sheets: CeoDebtImportSheet[];
  contracts: CeoDebtImportContract[];
  warnings: string[];
}

export type CeoDebtImportItem = CeoDebtImportContract & {
  /** `update` — только если есть `existing_id`. */
  action: 'create' | 'update';
  /** Графика нет — рассчитать по ставке и сроку. */
  build_schedule: boolean;
};

export interface CeoDebtImportRequest {
  items: CeoDebtImportItem[];
  past_payments_paid: boolean;
}

export interface CeoDebtImportResult {
  key: string;
  status: 'created' | 'updated' | 'error';
  id: string | null;
  message: string | null;
}

export interface CeoDebtImportResponse {
  results: CeoDebtImportResult[];
  created: number;
  updated: number;
  failed: number;
}

export interface CeoInflowPlanRow {
  id: string;
  period_month: string;
  expected_payment_date: string | null;
  project_id: string | null;
  contract_id: string | null;
  project_name: string;
  customer_name: string;
  contract_number: string;
  inflow_category: string;
  bdds_planned_amount: number;
  ks2_submitted_amount: number;
  ks3_signed_amount: number;
  ks3_doc_number: string | null;
  ks3_signed_date: string | null;
  advance_deduction: number;
  retention_deduction: number;
  expected_net_inflow: number;
  actual_received_amount: number;
  actual_received_date: string | null;
  probability_pct: number;
  status: string;
  delay_days: number;
  responsible_person: string | null;
  notes: string | null;
  unbilled_bdds_gap: number;
  ks3_receivable_balance: number;
  ks3_collection_pct: number;
  bdds_execution_pct: number;
}

export interface SmetaKs3BridgeItem {
  id: string;
  act_no: string | null;
  source_filename: string;
  period_to: string | null;
  total_period: number;
  advance_amount: number;
  retention_amount: number;
  net_payable: number;
  status: string;
}

export interface CeoInflowsAnalyticsSummary {
  total_bdds_planned: number;
  total_ks2_submitted: number;
  total_ks3_signed_gross: number;
  total_advance_deduction: number;
  total_retention_deduction: number;
  total_expected_net_ks3: number;
  total_actual_received: number;
  bdds_to_ks3_gap: number;
  ks3_awaiting_cash: number;
  overdue_ks3_receivables: number;
  bdds_fulfillment_pct: number;
  ks3_collection_pct: number;
  rows: CeoInflowPlanRow[];
  smeta_ks3_acts: SmetaKs3BridgeItem[];
}

export interface CeoPaymentApprovalRow {
  id: string;
  request_number: string;
  request_date: string;
  planned_pay_date: string;
  account_id: string | null;
  bank_name: string | null;
  account_number: string | null;
  project_id: string | null;
  project_name: string | null;
  contract_id: string | null;
  counterparty_name: string;
  counterparty_inn: string | null;
  amount: number;
  vat_amount: number;
  category: string;
  priority: 'critical' | 'high' | 'normal' | 'low' | string;
  purpose: string;
  initiator_name: string | null;
  budget_status: 'within_limit' | 'over_limit' | 'unbudgeted' | string;
  decision: 'pending' | 'approved' | 'deferred' | 'rejected' | 'executed' | string;
  decision_comment: string | null;
  decided_at: string | null;
  created_at: string;
  requested_amount?: number;
  approved_amount?: number;
  cut_amount?: number;
  paid_amount?: number;
  payment_execution_status?: 'unpaid' | 'partially_paid' | 'paid' | string;
  bank_doc_numbers?: string | null;
}

export interface CeoPaymentPositionSummary {
  available_cash_start: number;
  pending_approvals_total: number;
  pending_critical_total: number;
  approved_today_total: number;
  deferred_today_total: number;
  rejected_today_total: number;
  projected_cash_after_approved: number;
  projected_cash_after_all_pending: number;
  over_budget_requests_count: number;
  items: CeoPaymentApprovalRow[];
}

export interface CeoProjectHealthItem {
  project_id: string | null;
  project_name: string;
  customer_name: string;
  contract_number: string;
  bdds_planned: number;
  ks3_signed: number;
  expected_net_inflow: number;
  actual_received: number;
  receivable_gap: number;
  active_claims_count: number;
  active_claims_amount: number;
  overdue_instructions_count: number;
  health_status: 'good' | 'warning' | 'critical' | string;
}

export interface CeoExecutiveAlert {
  code: string;
  severity: 'critical' | 'warning' | 'info' | string;
  title: string;
  description: string;
  metric_value: string;
  target_tab: string;
}

export interface CeoExecutiveDashboard {
  generated_at: string;
  accounts_summary: CeoBankAccountsSummary;
  accounts: CeoBankAccount[];
  yesterday_payments: CeoDailyPaymentsSummary;
  debt_summary: CeoDebtPortfolioSummary;
  inflows_summary: CeoInflowsAnalyticsSummary;
  payment_position: CeoPaymentPositionSummary;
  project_matrix: CeoProjectHealthItem[];
  alerts: CeoExecutiveAlert[];
}

export interface BankApiSyncResult {
  synced_accounts: number;
  ingested_payments: number;
  total_balance_after: number;
  synced_at: string;
  /** Сколько подключений запущено на обмен с банком. */
  started_connections: number;
  /** Что не удалось принять из выписки. */
  warnings: string[];
}

export interface CeoBankConnection {
  id: string;
  provider: 'tochka_api' | 'alfa_api' | 'sber_business';
  label: string;
  status: 'pending' | 'connected' | 'error';
  /** Тестовый контур банка: проверяет доступ, в обмен и учёт не попадает. */
  sandbox: boolean;
  fingerprint: string;
  expires_at: string | null;
  next_sync_at: string | null;
  syncing: boolean;
  last_sync_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  accounts_count: number;
  rotated_at: string | null;
  created_at: string;
}

export interface CeoBankConnectionsResponse {
  /** Есть ключ шифрования на сервере: без него подключения недоступны. */
  vault_ready: boolean;
  can_manage: boolean;
  connections: CeoBankConnection[];
}

export interface CeoBankConnectResult {
  connection: CeoBankConnection;
  accounts_found: number;
  /** Куда отправить человека, чтобы завершить подключение входом в банк. */
  authorize_url: string | null;
  /** Итог проверки тестового контура. */
  check: string | null;
}

export interface BankCredentialsInput {
  token?: string;
  certificate?: string;
  /** Контейнер .p12 в base64 и его пароль. */
  certificate_p12?: string;
  certificate_password?: string;
  client_id?: string;
  client_secret?: string;
  /** Токен обновления из кабинета Sber API. */
  refresh_token?: string;
  redirect_uri?: string;
  sandbox?: boolean;
}

const ceoApi = osnovaApi.injectEndpoints({
  endpoints: (build) => ({
    getCeoDashboard: build.query<CeoExecutiveDashboard, void>({
      query: () => '/agents/ceo/dashboard',
      providesTags: ['CeoDashboard', 'CeoBankAccount', 'CeoPayment', 'CeoDebt', 'CeoInflow', 'CeoApproval'],
    }),

    listCeoBankAccounts: build.query<
      {
        summary: CeoBankAccountsSummary;
        accounts: CeoBankAccount[];
        balance_history: CeoBankBalanceHistoryRow[];
      },
      void
    >({
      query: () => '/agents/ceo/bank-accounts',
      providesTags: ['CeoBankAccount'],
    }),

    createCeoBankAccount: build.mutation<
      CeoBankAccount,
      {
        bank_name: string;
        bik?: string;
        account_number: string;
        corr_account?: string;
        account_type?: string;
        entity_name?: string;
        entity_inn?: string;
        entity_kpp?: string;
        purpose_label?: string;
        current_balance?: number;
        available_balance?: number;
        blocked_amount?: number;
        overdraft_limit?: number;
        interest_rate_pct?: number;
        api_provider?: string;
        is_primary?: boolean;
        notes?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/ceo/bank-accounts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CeoBankAccount', 'CeoDashboard', 'FinancierDashboard'],
    }),

    /** Реквизиты владельца счёта для платёжек; остатки и провайдер не трогаются. */
    updateCeoBankAccountRequisites: build.mutation<
      CeoBankAccount,
      {
        id: string;
        bik: string;
        corr_account: string;
        entity_name: string;
        entity_inn?: string;
        entity_kpp?: string;
        account_type?: string;
        purpose_label?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/ceo/bank-accounts/${id}/requisites`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CeoBankAccount', 'CeoDashboard', 'FinancierDashboard'],
    }),

    syncCeoBankAccountsApi: build.mutation<
      BankApiSyncResult,
      { account_id?: string } | void
    >({
      query: (body) => ({
        url: '/agents/ceo/bank-accounts/sync-api',
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: [
        'CeoBankAccount',
        'CeoPayment',
        'CeoDashboard',
        'FinancierDashboard',
        'FinancierRequest',
        'FinancierRegistry',
      ],
    }),

    listCeoBankConnections: build.query<CeoBankConnectionsResponse, void>({
      query: () => '/agents/ceo/bank-connections',
      providesTags: ['CeoBankConnection'],
    }),

    createCeoBankConnection: build.mutation<
      CeoBankConnectResult,
      BankCredentialsInput & { provider: string; label: string }
    >({
      query: (body) => ({
        url: '/agents/ceo/bank-connections',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CeoBankConnection', 'CeoBankAccount'],
    }),

    replaceCeoBankConnectionSecret: build.mutation<
      CeoBankConnectResult,
      BankCredentialsInput & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/ceo/bank-connections/${id}/secret`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CeoBankConnection', 'CeoBankAccount'],
    }),

    deleteCeoBankConnection: build.mutation<{ unlinked_accounts: number }, string>({
      query: (id) => ({
        url: `/agents/ceo/bank-connections/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CeoBankConnection', 'CeoBankAccount'],
    }),

    syncCeoBankConnection: build.mutation<CeoBankConnection, string>({
      query: (id) => ({
        url: `/agents/ceo/bank-connections/${id}/sync`,
        method: 'POST',
      }),
      invalidatesTags: ['CeoBankConnection'],
    }),

    finishCeoBankOauth: build.mutation<CeoBankConnectResult, { code: string; state: string }>({
      query: (body) => ({
        url: '/agents/ceo/bank-connections/oauth-callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CeoBankConnection', 'CeoBankAccount'],
    }),

    /** Остаток вручную — для счёта, по которому нет выписки. */
    setCeoBankBalance: build.mutation<
      BankApiSyncResult,
      { id: string; snapshot_date: string; closing_balance: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/ceo/bank-accounts/${id}/balance`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CeoBankAccount', 'CeoDashboard', 'FinancierDashboard'],
    }),

    importCeo1CStatement: build.mutation<
      BankApiSyncResult,
      { content: string; bank_name?: string }
    >({
      query: (body) => ({
        url: '/agents/ceo/bank-accounts/import-1c',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'CeoBankAccount',
        'CeoPayment',
        'CeoDashboard',
        'FinancierDashboard',
        'FinancierRequest',
        'FinancierRegistry',
      ],
    }),

    getCeoDailyPayments: build.query<
      CeoDailyPaymentsSummary,
      { date?: string; account_id?: string; direction?: string; category?: string } | void
    >({
      query: (params) => ({
        url: '/agents/ceo/payments/daily',
        params: params ?? undefined,
      }),
      providesTags: ['CeoPayment'],
    }),

    createCeoBankPayment: build.mutation<
      CeoBankPayment,
      {
        account_id?: string;
        payment_date: string;
        direction: 'inflow' | 'outflow';
        amount: number;
        vat_amount?: number;
        counterparty_name: string;
        counterparty_inn?: string;
        purpose: string;
        category?: string;
        document_number?: string;
        ks3_reference?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/ceo/payments',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'CeoPayment',
        'CeoBankAccount',
        'CeoDashboard',
        'FinancierDashboard',
        'FinancierRequest',
      ],
    }),

    getCeoDebtsPortfolio: build.query<
      CeoDebtPortfolioSummary,
      { instrument_type?: string } | void
    >({
      query: (params) => ({
        url: '/agents/ceo/debts',
        params: params ?? undefined,
      }),
      providesTags: ['CeoDebt'],
    }),

    getCeoDebtDetail: build.query<CeoDebtInstrumentDetail, string>({
      query: (id) => `/agents/ceo/debts/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'CeoDebt', id }],
    }),

    createCeoDebtInstrument: build.mutation<
      CeoDebtInstrumentDetail,
      {
        instrument_type: string;
        contract_number: string;
        title: string;
        creditor_name: string;
        creditor_inn?: string;
        total_limit: number;
        drawn_amount: number;
        principal_balance: number;
        rate_type: string;
        base_rate_pct: number;
        margin_pct?: number;
        start_date: string;
        end_date: string;
        payment_day?: number;
        leased_asset_name?: string;
        leased_asset_count?: number;
        residual_buyout_amount?: number;
        collateral_summary?: string;
        covenants_summary?: string;
        responsible_person?: string;
        notes?: string;
        auto_generate_schedule?: boolean;
        schedule_mode?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/ceo/debts',
        method: 'POST',
        body,
      }),
      // Ковенанты из карточки — на пульте Финансиста
      invalidatesTags: ['CeoDebt', 'CeoDashboard', 'FinancierDashboard'],
    }),

    /**
     * Оплата платежа по графику: сумма прибавляется к уже оплаченному (больше остатка —
     * отказ), без суммы — весь остаток, без даты — сегодня.
     */
    markCeoDebtSchedulePaid: build.mutation<
      CeoDebtScheduleItem,
      {
        item_id: string;
        paid_amount?: number;
        paid_date?: string;
        payment_reference?: string;
        comment?: string;
      }
    >({
      query: ({ item_id, ...body }) => ({
        url: `/agents/ceo/debts/schedule/${item_id}/mark-paid`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CeoDebt', 'CeoDashboard'],
    }),

    /** Снять отметку оплаты платежа по графику; оплату заявкой Фабрики сервер не снимет. */
    unmarkCeoDebtSchedulePaid: build.mutation<CeoDebtScheduleItem, string>({
      query: (item_id) => ({
        url: `/agents/ceo/debts/schedule/${item_id}/unmark-paid`,
        method: 'POST',
      }),
      invalidatesTags: ['CeoDebt', 'CeoDashboard'],
    }),

    /**
     * Разбор файла Excel/CSV (поле `file`): договоры и графики платежей на
     * просмотр. Ничего не сохраняет — теги не трогает.
     */
    parseCeoDebtImportFile: build.mutation<{ preview: CeoDebtImportPreview }, FormData>({
      query: (form) => ({
        url: '/agents/ceo/debts/import/parse',
        method: 'POST',
        body: form,
      }),
    }),

    /** Отмеченные договоры из файла: новые создаются, найденные в ОСНОВЕ — обновляются. */
    importCeoDebts: build.mutation<CeoDebtImportResponse, CeoDebtImportRequest>({
      query: (body) => ({
        url: '/agents/ceo/debts/import',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CeoDebt', 'CeoDashboard', 'FinancierDashboard'],
    }),

    /** Договор и его график удаляются; платежи по выпискам остаются, но отвязываются от договора. */
    deleteCeoDebtInstrument: build.mutation<{ ok: true }, string>({
      query: (id) => ({
        url: `/agents/ceo/debts/${id}`,
        method: 'DELETE',
      }),
      // Платежи по выпискам отвязываются от договора — их подписи тоже обновить.
      invalidatesTags: ['CeoDebt', 'CeoDashboard', 'FinancierDashboard', 'CeoPayment'],
    }),

    getCeoInflows: build.query<
      CeoInflowsAnalyticsSummary,
      { period_month?: string; status?: string } | void
    >({
      query: (params) => ({
        url: '/agents/ceo/inflows',
        params: params ?? undefined,
      }),
      providesTags: ['CeoInflow'],
    }),

    createCeoInflowPlan: build.mutation<
      CeoInflowPlanRow,
      {
        period_month?: string;
        expected_payment_date?: string;
        project_name: string;
        customer_name: string;
        contract_number?: string;
        inflow_category?: string;
        bdds_planned_amount: number;
        ks2_submitted_amount?: number;
        ks3_signed_amount?: number;
        ks3_doc_number?: string;
        ks3_signed_date?: string;
        advance_deduction?: number;
        retention_deduction?: number;
        actual_received_amount?: number;
        responsible_person?: string;
        notes?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/ceo/inflows',
        method: 'POST',
        body,
      }),
      // План прихода — в календаре пульта Финансиста, КС-3 — в исходящем НДС
      invalidatesTags: ['CeoInflow', 'CeoDashboard', 'FinancierDashboard', 'FinancierTaxCalendar'],
    }),

    updateCeoInflowFact: build.mutation<
      CeoInflowPlanRow,
      {
        id: string;
        ks3_signed_amount?: number;
        ks3_doc_number?: string;
        advance_deduction?: number;
        retention_deduction?: number;
        expected_net_inflow?: number;
        actual_received_amount?: number;
        actual_received_date?: string;
        status?: string;
        notes?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/ceo/inflows/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CeoInflow', 'CeoDashboard', 'FinancierDashboard', 'FinancierTaxCalendar'],
    }),

    getCeoPaymentApprovals: build.query<CeoPaymentPositionSummary, void>({
      query: () => '/agents/ceo/approvals',
      providesTags: ['CeoApproval'],
    }),

    createCeoPaymentApproval: build.mutation<
      CeoPaymentApprovalRow,
      {
        request_number?: string;
        planned_pay_date?: string;
        account_id?: string;
        counterparty_name: string;
        counterparty_inn?: string;
        amount: number;
        vat_amount?: number;
        category?: string;
        priority?: string;
        purpose: string;
        initiator_name?: string;
        budget_status?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/ceo/approvals',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'CeoApproval',
        'CeoDashboard',
        'FinancierRequest',
        'FinancierDashboard',
      ],
    }),

    decideCeoPaymentApproval: build.mutation<
      CeoPaymentApprovalRow,
      {
        id: string;
        decision: 'approved' | 'deferred' | 'rejected' | 'executed' | 'pending';
        decision_comment?: string;
        approved_amount?: number;
        carryover_remainder?: boolean;
        next_pay_date?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/ceo/approvals/${id}/decision`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        'CeoApproval',
        'CeoDashboard',
        'FinancierRequest',
        'FinancierRegistry',
        'FinancierPayrollBatch',
        'FinancierDashboard',
      ],
    }),
  }),
});

export const {
  useGetCeoDashboardQuery,
  useListCeoBankAccountsQuery,
  useCreateCeoBankAccountMutation,
  useUpdateCeoBankAccountRequisitesMutation,
  useSyncCeoBankAccountsApiMutation,
  useImportCeo1CStatementMutation,
  useListCeoBankConnectionsQuery,
  useCreateCeoBankConnectionMutation,
  useReplaceCeoBankConnectionSecretMutation,
  useDeleteCeoBankConnectionMutation,
  useSyncCeoBankConnectionMutation,
  useFinishCeoBankOauthMutation,
  useSetCeoBankBalanceMutation,
  useGetCeoDailyPaymentsQuery,
  useCreateCeoBankPaymentMutation,
  useGetCeoDebtsPortfolioQuery,
  useGetCeoDebtDetailQuery,
  useCreateCeoDebtInstrumentMutation,
  useMarkCeoDebtSchedulePaidMutation,
  useUnmarkCeoDebtSchedulePaidMutation,
  useParseCeoDebtImportFileMutation,
  useImportCeoDebtsMutation,
  useDeleteCeoDebtInstrumentMutation,
  useGetCeoInflowsQuery,
  useCreateCeoInflowPlanMutation,
  useUpdateCeoInflowFactMutation,
  useGetCeoPaymentApprovalsQuery,
  useCreateCeoPaymentApprovalMutation,
  useDecideCeoPaymentApprovalMutation,
} = ceoApi;
